import React, { useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ServiceTitanBusinessUnit } from '_lib/api/apiTypes.shim';

import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

export interface BusinessUnitSelectProps extends FieldIssueProps {
  companyId: string;
  onSetBusinessUnit: (businessUnit: ServiceTitanBusinessUnit) => void;
  businessUnit?: ServiceTitanBusinessUnit;
  jobTypeBusinessUnitIds?: string[];
  disabled?: boolean;
}

export const BusinessUnitSelect = (props: BusinessUnitSelectProps) => {
  const { companyId, onSetBusinessUnit, businessUnit, jobTypeBusinessUnitIds, disabled, issuePath, issues } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);
  const businessUnitsQuery = serviceTitanApi.businessUnits.useUnpagedListCached();

  if (businessUnitsQuery.error) {
    return (
      <ManualErrorBoundary
        errorState={{ message: 'Failed to load Business Units' }}
        onRefresh={async () => {
          await businessUnitsQuery.refetch();
        }}
      />
    );
  }

  const filteredBusinessUnitQueryData = businessUnitsQuery.data?.filter(
    (bu) => bu.isActive && (!jobTypeBusinessUnitIds || jobTypeBusinessUnitIds.find((buId) => buId === bu.providerId))
  );

  return (
    <TextField
      fullWidth
      label='Business Unit'
      required
      disabled={disabled}
      value={businessUnit?.providerId || ''}
      defaultValue={''}
      select
      error={{
        issues,
        issuePath
      }}
      onChange={(e) => {
        const selectedBusinessUnit = filteredBusinessUnitQueryData?.find((c) => c.providerId === e.target.value);
        if (selectedBusinessUnit) onSetBusinessUnit(selectedBusinessUnit);
      }}>
      {filteredBusinessUnitQueryData &&
        filteredBusinessUnitQueryData?.length > 0 &&
        filteredBusinessUnitQueryData.map(
          (businessUnit) =>
            businessUnit.isActive && (
              <MenuItem key={businessUnit.providerId} value={businessUnit.providerId}>
                <Typography text={businessUnit.name} />
              </MenuItem>
            )
        )}
      {(!filteredBusinessUnitQueryData || filteredBusinessUnitQueryData?.length === 0) && (
        <MenuItem disabled>No Business Units found.</MenuItem>
      )}
    </TextField>
  );
};
